import React, { useEffect, useState } from 'react'
import ModalTest from '../../../BaseModal'
import * as S from '../../../styles/modal-style'
import { X, Plus } from 'lucide-react'
import SelectAndSearch from '../../../SelectAndSearch'
import axios from '../../../../config/axios'
import InputFiles from '../../../ui/InputFiles'
import LoadImages from '../../../ui/LoadImages'
import CreateProductionSectorModal from '../../CreateProductionSectorModal'

interface AddServiceModalProps {
  id: number
  setModalActive: React.Dispatch<React.SetStateAction<number | null>>
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>
  serviceToEdit?: any
  setServiceToEdit: React.Dispatch<React.SetStateAction<any>>
}

export interface ServiceToAddType {
  id?: number
  stageId: number
  name: string
  image: File | null
  description: string
  observation: string
  parentId?: number | null
  parentStageId?: number | null
}

const AddServiceModal = ({ setModalActive, id, setRefresh, serviceToEdit, setServiceToEdit }: AddServiceModalProps) => {
  const [services, setServices] = useState<Partial<ServiceToAddType>[]>([])
  const [formData, setFormData] = useState<ServiceToAddType>({
    stageId: 0,
    name: '',
    description: '',
    image: null,
    observation: ''
  })
  const [inactiveButton, setInactiveButton] = useState<boolean>(false)
  const [isSectorModalOpen, setIsSectorModalOpen] = useState(false)
  const [newServiceNameInput, setNewServiceNameInput] = useState('')

  useEffect(() => {
    if (formData.description !== '' && formData.observation !== '' && formData.stageId !== 0) {
      setInactiveButton(false)
    } else {
      setInactiveButton(true)
    }
  }, [formData])

  const handleSelectService = (service: Partial<ServiceToAddType>) => {
    setFormData(prev => ({
      ...prev,
      stageId: service.id || 0,
      name: service.name || '',
      description: service.description || '',
      observation: service.observation || '',
    }))
    setInactiveButton(true)
  }

  const handleChangeInfos = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
    setInactiveButton(true)
  }

  const handleAddImage = (file: File) => {
    setFormData(prev => ({
      ...prev,
      image: file
    }))
  }

  const handleAddService = async () => {
    if (inactiveButton) return
    try {
      setInactiveButton(true)
      const parentId = serviceToEdit?.parentStageId ?? serviceToEdit?.parentId ?? null
      const response = await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/${id}/stage`,
        {
          stageId: formData.stageId,
          description: formData.description,
          observation: formData.observation,
          parentId,
        },
      )
      if (response.data) {
        setServiceToEdit(null)
        setModalActive(null)
        setRefresh((prev) => !prev)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsInactiveButton(false)
    }
  }

  const handleEditService = async () => {
    if (inactiveButton) return
    try {
      setInactiveButton(true)
      const response = await axios.patch(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/edit-stage/${id}`,
        {
          stageId: formData.stageId,
          id: formData.id,
          description: formData.description,
          observation: formData.observation,
          parentId: formData.parentId ?? serviceToEdit?.parentId ?? null,
        },
      )
      if (response.data) {
        setServiceToEdit(null)
        setModalActive(null)
        setRefresh(prev => !prev)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setInactiveButton(false)
    }
  }

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/stages`)
      if (response.data) {
        const filtered = (response.data || []).filter((s: any) => {
          const name = (s.name || '').trim()
          const nameLower = name.toLowerCase()
          const areaName = (s.Sector?.Area?.name || s.sector?.Area?.name || s.sector?.area?.name || s.Area?.name || '').toLowerCase()

          if (areaName) {
            if (
              areaName.includes('mostruá') ||
              areaName.includes('mostrua') ||
              areaName.includes('modelag') ||
              areaName.includes('pilotag') ||
              areaName.includes('desenvolvim')
            ) {
              return false
            }
          }

          const isMostruario = name.startsWith('(M)') || nameLower.includes('mostruário') || nameLower.includes('mostruario')
          const isPilotModelagem = name.startsWith('(P)') || nameLower.includes('modelagem') || nameLower.includes('pilotagem') || nameLower.includes('desenvolvimento')
          const isPrototipo = nameLower.startsWith('protótipo') || nameLower.startsWith('prototipo')
          const isRefName = nameLower.includes('referência') || nameLower.includes('referencia')

          return !isMostruario && !isPilotModelagem && !isPrototipo && !isRefName
        })
        setServices(filtered)
      }
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    fetchServices()
  }, [])

  useEffect(() => {
    if (serviceToEdit) {
      setFormData({
        id: serviceToEdit.id,
        stageId: serviceToEdit.stageId,
        name: serviceToEdit.name,
        description: serviceToEdit.description,
        observation: serviceToEdit.observation,
        image: serviceToEdit.image
      })
    } else {
      setFormData({
        stageId: 0,
        name: '',
        description: '',
        image: null,
        observation: ''
      })
    }
  }, [serviceToEdit])

  const handleConfirmCreateService = async (sectorId: number) => {
    if (!newServiceNameInput.trim()) return
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/stages`,
        {
          name: newServiceNameInput.trim(),
          sectorId,
        },
      )
      const createdStage = response.data
      setIsSectorModalOpen(false)
      setNewServiceNameInput('')
      await fetchServices()

      if (createdStage && createdStage.id) {
        setFormData((prev) => ({
          ...prev,
          stageId: createdStage.id,
          name: createdStage.name,
        }))
      }
    } catch (err) {
      console.error('Erro ao criar novo serviço de produção:', err)
    }
  }

  const setIsInactiveButton = (val: boolean) => setInactiveButton(val)

  return (
    <>
      <ModalTest>
        <S.ContainerOfModal width='42rem'>
          <S.Header>
            <S.HeaderTitle>
              {serviceToEdit?.id
                ? 'Editar Serviço'
                : serviceToEdit?.parentStageId
                  ? 'Adicionar subetapa'
                  : 'Adicionar Serviço'}
            </S.HeaderTitle>
            <S.CloseDiv onClick={() => {
              setServiceToEdit(null)
              setModalActive(null)
            }}> <X size={20} /></S.CloseDiv>
          </S.Header>
          <S.FormContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
              <SelectAndSearch label='Selecione o serviço' options={services} onSelect={handleSelectService} selectedOption={{ id: formData.stageId, name: formData.name }} />
              <button
                type="button"
                onClick={() => {
                  const promptName = window.prompt('Digite o nome do novo serviço de produção:')
                  if (promptName && promptName.trim()) {
                    setNewServiceNameInput(promptName.trim())
                    setIsSectorModalOpen(true)
                  }
                }}
                style={{
                  alignSelf: 'flex-end',
                  background: 'none',
                  border: 'none',
                  color: '#0090d3',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '2px 0',
                }}
              >
                <Plus size={14} /> + Criar novo serviço de produção
              </button>
            </div>
            <S.InputDiv>
              <S.Label>Descrição detalhada</S.Label>
              <S.Textarea placeholder='Digite aqui' name='description' value={formData.description} onChange={handleChangeInfos} />
            </S.InputDiv>
            <S.InputDiv>
              <S.Label>Observações</S.Label>
              <S.Textarea placeholder='Digite aqui' name='observation' value={formData.observation} onChange={handleChangeInfos} />
            </S.InputDiv>
          </S.FormContainer>
          <S.BottomDiv>
            <S.ButtonContainer>
              <S.Button onClick={() => {
                setServiceToEdit(null)
                setModalActive(null)
              }} className='cancel'>Cancelar</S.Button>
              <S.Button
                onClick={serviceToEdit?.id ? handleEditService : handleAddService}
                className={inactiveButton ? 'inactive' : ''}
              >
                {serviceToEdit?.id
                  ? 'Salvar Alterações'
                  : serviceToEdit?.parentStageId
                    ? 'Adicionar subetapa'
                    : 'Adicionar Serviço'}
              </S.Button>
            </S.ButtonContainer>
          </S.BottomDiv>
        </S.ContainerOfModal>
      </ModalTest>

      <CreateProductionSectorModal
        isOpen={isSectorModalOpen}
        onClose={() => setIsSectorModalOpen(false)}
        onConfirm={handleConfirmCreateService}
        serviceName={newServiceNameInput}
      />
    </>
  )
}

export default AddServiceModal